import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';

export default function TopSellingProducts() {
  const { user, companyId } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [companyName, setCompanyName] = useState('');
  const perPage = 10;
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const formatVariantLabel = (variant) => {
    if (!variant) return '';
    if (variant.name) return variant.name;
    if (variant.label) return variant.label;
    if (Array.isArray(variant.options) && variant.options.length) {
      return variant.options.map(o => `${o.group}: ${o.value}`).join(' / ');
    }
    return '';
  };

  const getInventoryInfo = (item, inventory) => {
    const productId = item.id || item.productId;
    const variantId = item.variantId || item.variant_id;
    const invProduct =
      inventory.find(p => p.id === productId) ||
      inventory.find(p => p.name === item.name);

    let variant = null;
    if (invProduct && variantId && Array.isArray(invProduct.variants)) {
      variant = invProduct.variants.find(
        (v) => v.id === variantId || v.variantId === variantId
      );
    }

    const variantLabel = formatVariantLabel(variant) || item.variantName || '';
    // Display product SKU even for variants; fall back to variant/item only if product SKU missing
    const sku = invProduct?.sku || item.sku || variant?.sku || variant?.variantSku || variant?.barcode || '-';
    const availableQty = variant
      ? toNumber(variant.qty ?? variant.quantity ?? 0)
      : toNumber(invProduct?.qty ?? invProduct?.quantity ?? 0);

    return { invProduct, variant, variantLabel, sku, availableQty, productId, variantId };
  };

const loadPlan = async () => {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
  }
};

  useEffect(() => {
    loadPlan();
    // Set today's date as default for both from and to date
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const loadTopProducts = async () => {
    if (!user || !companyId) return;

    // 1. Load inventory
    const inventorySnap = await getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'products'));
    const inventory = inventorySnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

    // 2. Load orders (only non-void)
    const q = query(
      collection(db, 'companies', companyId, 'orders'),
      where('paymentStatus', '!=', 'Void'),
      orderBy('paymentStatus'),
      orderBy('timestamp', 'desc')
    );
    const orderSnap = await getDocs(q);
    const orders = orderSnap.docs.map(doc => doc.data());

    // 3. Process sold items with date filtering
    const soldMap = {};
    orders.forEach(order => {
      const orderDate = parseTimestamp(order.timestamp);
      if (!orderDate) return;
      const from = fromDate ? new Date(fromDate) : new Date(0);
      const to = toDate ? new Date(toDate) : new Date();
      to.setHours(23, 59, 59, 999); // Include entire day

      if (orderDate >= from && orderDate <= to) {
        order.items.forEach(item => {
          const isBundle = item.type === 'bundle' || (item.items && Array.isArray(item.items));
          // Bundle: count each child as its own product sold
          // Check if item is a bundle (has items array)
          if (isBundle) {
            // Process each item in the bundle
            item.items.forEach(bundleItem => {
              const info = getInventoryInfo(bundleItem, inventory);
              const key = `${info.productId || bundleItem.name}|${info.variantId || ''}`;
              const baseName = bundleItem.name;
              const variantLabel = info.variantLabel || '';
              if (!soldMap[key]) {
                soldMap[key] = {
                  name: baseName,
                  variantLabel,
                  sku: info.sku,
                  qtySold: 0,
                  availableQty: info.availableQty,
                };
              }
              soldMap[key].qtySold += toNumber(bundleItem.quantity || 1) * toNumber(item.qty || item.quantity || 1);
            });
          } else {
            // Regular product
            const info = getInventoryInfo(item, inventory);
            const baseName = item.name;
            const variantLabel = info.variantLabel || '';
            const key = `${info.productId || baseName}|${info.variantId || ''}`;
            if (!soldMap[key]) {
              soldMap[key] = {
                name: baseName,
                variantLabel,
                sku: info.sku,
                qtySold: 0,
                availableQty: info.availableQty,
              };
            }
            soldMap[key].qtySold += toNumber(item.quantity || item.qty || 0);
          }
        });
      }
    });

    // 4. Merge inventory + sales
    const merged = Object.values(soldMap).map(entry => ({
      sku: entry.sku || '-',
      name: entry.name,
      variantLabel: entry.variantLabel,
      qtySold: entry.qtySold,
      availableQty: entry.availableQty,
    }));

    // Collapse non-variant items (including bundle children) by SKU+Name
    const nonVariantMap = new Map();
    const withVariants = [];

    merged.forEach((entry) => {
      if (entry.variantLabel) {
        withVariants.push(entry);
        return;
      }
      const key = `${(entry.sku || '-').toLowerCase()}::${entry.name.toLowerCase()}`;
      const existing = nonVariantMap.get(key);
      if (existing) {
        existing.qtySold += entry.qtySold;
        if (entry.availableQty !== undefined && entry.availableQty !== null) {
          existing.availableQty = entry.availableQty;
        }
      } else {
        nonVariantMap.set(key, { ...entry });
      }
    });

    const combined = [
      ...Array.from(nonVariantMap.values()),
      ...withVariants,
    ].sort((a, b) => b.qtySold - a.qtySold);

    setProducts(combined);
  };

  const loadCompanyName = async () => {
    if (!user || !companyId) return;
    const snap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
    if (snap.exists()) setCompanyName(snap.data().companyName || 'Company');
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  const exportToPDF = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }
  
    const doc = new jsPDF();
    const title = `Top Selling Products Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;

    doc.setFontSize(12);
    doc.text(companyName, 105, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.text(title, 105, 22, { align: 'center' });

    autoTable(doc, {
      head: [['SKU', 'Product', 'Quantity Sold', 'Available Stock']],
      body: filtered.map(p => [
        p.sku,
        `${p.name}${p.variantLabel ? `\nVariant: ${p.variantLabel}` : ''}`,
        p.qtySold,
        p.availableQty
      ]),
      startY: 30,
      styles: { fontSize: 9 },
    });

    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(9);
    doc.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });

    const file = `top-selling-products_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`.replace(/\//g, '-');
    doc.save(file);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadTopProducts(), loadCompanyName()]);
      setLoading(false);
    };
    loadAll();
  }, [user, fromDate, toDate]);

  if (loading) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading information...</span>
      </div>
    );
  }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Top Selling Products</h2>

      <div className="flex gap-4 mb-6 flex-wrap">
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <span className="text-gray-400">to</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <input type="text" placeholder="Search by name or SKU" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <button onClick={exportToPDF} className="bg-purple-600 px-4 py-2 rounded">Export to PDF</button>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="px-4 py-2">SKU</th>
              <th className="px-4 py-2">Product</th>
              <th className="px-4 py-2">Quantity Sold</th>
              <th className="px-4 py-2">Available Stock</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((item, i) => (
              <tr key={i} className="border-t border-gray-700">
                <td className="px-4 py-2">{item.sku}</td>
                <td className="px-4 py-2">
                  <div className="text-white">{item.name}</div>
                  {item.variantLabel && (
                    <div className="text-xs text-gray-400">Variant: {item.variantLabel}</div>
                  )}
                </td>
                <td className="px-4 py-2">{item.qtySold}</td>
                <td className="px-4 py-2">{item.availableQty}</td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-gray-400" colSpan={4}>No data found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center flex-wrap mt-6 gap-2">
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded ${currentPage === i + 1 ? 'bg-purple-600 text-white' : 'bg-[#2c2c2c] text-gray-300'}`}
            onClick={() => setCurrentPage(i + 1)}
          >{i + 1}</button>
        ))}
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}
